import { MouseEvent, useMemo, useState } from "react";

import SignalBubbleButton from "./SignalBubbleButton";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTruncatedList } from "@/hooks/useTruncatedList";
import { type SidebarSignalBubble } from "@/routes/request-logs/search/signal-search-utils";

interface SidebarSignalBubblesProps {
  signals: SidebarSignalBubble[];
  active: boolean;
  onSignalClick: (event: MouseEvent<HTMLButtonElement>, signal: SidebarSignalBubble) => void;
}

const SidebarSignalBubbles = ({ signals, active, onSignalClick }: SidebarSignalBubblesProps) => {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const recalcKey = useMemo(
    () => signals.map(signal => `${signal.signalId}:${signal.name}:${signal.isTrue}`).join("|"),
    [signals],
  );
  const { containerRef, measureRef, visibleCount, hiddenCount } = useTruncatedList({
    itemCount: signals.length,
    gapPx: 4,
    overflowIndicatorWidth: 38,
    keepMinOne: true,
    recalcKey,
  });

  const visibleSignals = signals.slice(0, visibleCount);
  const hiddenSignals = signals.slice(visibleCount);

  const handleOverflowMouseEnter = () => setOverflowOpen(true);

  const handleOverflowMouseLeave = (event: MouseEvent<HTMLElement>) => {
    const isOverflowTarget =
      event.relatedTarget instanceof HTMLElement &&
      Boolean(
        event.relatedTarget.closest(
          "[data-sidebar-signal-overflow], [data-sidebar-signal-overflow-trigger]",
        ),
      );

    if (isOverflowTarget) {
      return;
    }

    setOverflowOpen(false);
  };

  if (signals.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="relative flex min-w-0 max-w-full flex-nowrap items-center gap-1 overflow-hidden"
    >
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute left-0 top-0 flex flex-nowrap items-center gap-1"
        aria-hidden="true"
      >
        {signals.map(signal => (
          <SignalBubbleButton
            key={signal.signalId}
            signal={signal}
            active={active}
            onSignalClick={onSignalClick}
          />
        ))}
      </div>
      {visibleSignals.map(signal => (
        <SignalBubbleButton
          key={signal.signalId}
          signal={signal}
          active={active}
          onSignalClick={onSignalClick}
        />
      ))}
      {hiddenCount > 0 ? (
        <Popover open={overflowOpen} onOpenChange={setOverflowOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-sidebar-signal-overflow-trigger="true"
              onMouseEnter={handleOverflowMouseEnter}
              onMouseLeave={handleOverflowMouseLeave}
              onMouseDown={event => event.stopPropagation()}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
              className="inline-flex h-6 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-600 shadow-sm shadow-slate-200/60 transition-colors hover:border-slate-300 hover:bg-slate-50"
              aria-label={`Show ${hiddenCount} more signals`}
            >
              +{hiddenCount}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={4}
            className="z-[120] w-auto min-w-[180px] max-w-[240px] overflow-hidden rounded-lg border border-slate-200 bg-white p-0 shadow-lg shadow-slate-200/70"
            data-sidebar-signal-overflow="true"
            onOpenAutoFocus={event => event.preventDefault()}
            onCloseAutoFocus={event => event.preventDefault()}
            onMouseEnter={handleOverflowMouseEnter}
            onMouseLeave={handleOverflowMouseLeave}
          >
            <div className="border-b border-slate-100 px-2.5 py-2">
              <div className="text-xs font-semibold text-slate-900">Hidden signals</div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                {hiddenCount} more signal{hiddenCount === 1 ? "" : "s"}. Select one to filter by it.
              </div>
            </div>
            <div className="flex max-h-40 flex-wrap gap-1 overflow-y-auto p-2">
              {hiddenSignals.map(signal => (
                <SignalBubbleButton
                  key={signal.signalId}
                  signal={signal}
                  active={active}
                  onSignalClick={onSignalClick}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
};

export default SidebarSignalBubbles;
